import { useState, useEffect, useRef } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import db from "../../../database";
import { getRetailStores } from "../../../services/generic";

/**
 * Manages customer search, order history loading, product fetching,
 * and store selection for the call center.
 *
 * @param {Object} params
 * @param {Object} params.tillData
 * @param {boolean} params.callCenterVisible
 * @param {Object} params.cart
 * @param {Function} params.setCart
 * @param {Function} params.setLoading
 * @param {Function} params.searchOrderHistory
 * @returns {Object}
 */
const useCallCenterCustomer = ({
  tillData,
  callCenterVisible,
  cart,
  setCart,
  setLoading,
  searchOrderHistory,
}) => {
  const [productsList, setProductsList] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productCategoriesList, setProductCategoriesList] = useState([]);
  const [selectCategoryList, setSelectCategoryList] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [retailStores, setRetailStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeModalVisible, setStoreModalVisible] = useState(false);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);

  const hasLoadedOrderHistory = useRef(false);
  const storesLoaded = useRef(false);
  const ITEMS_PER_LOAD = 50;
  const scrollContainerRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Paginate displayed products from filteredProducts
  useEffect(() => {
    setDisplayedProducts(filteredProducts.slice(0, ITEMS_PER_LOAD));
    setHasMoreProducts(filteredProducts.length > ITEMS_PER_LOAD);
  }, [filteredProducts]);

  // Scroll-based lazy loading
  const handleScrollLoad = (event) => {
    const scrollEl = event.target;
    const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
    const currentScroll = scrollEl.scrollTop;

    if (Math.round(currentScroll) >= Math.round(maxScroll - 100) && hasMoreProducts) {
      const lengthOff = displayedProducts.length;
      if (lengthOff < filteredProducts.length) {
        clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
          const nextBatch = filteredProducts.slice(lengthOff, lengthOff + ITEMS_PER_LOAD);
          setDisplayedProducts((prev) => [...prev, ...nextBatch]);
          if (lengthOff + nextBatch.length >= filteredProducts.length) {
            setHasMoreProducts(false);
          }
        }, 200);
      }
    }
  };

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (scrollEl) {
      scrollEl.addEventListener("scroll", handleScrollLoad);
      return () => scrollEl.removeEventListener("scroll", handleScrollLoad);
    }
  }, [displayedProducts, filteredProducts]);

  // Fetch products and categories on mount
  useEffect(() => {
    const fetchProductsAndCategories = async () => {
      try {
        const products = await db.products
          .filter((product) => product.isactive == true)
          .filter((product) => product.productSegment !== "BG")
          .toArray();

        if (products?.length > 0) {
          setProductsList(products);
          setFilteredProducts(products);

          const categories = await db.productCategories.toArray();
          const filteredCategories = categories.filter((cat) => products.some((p) => p.mProductCategoryId === cat.mProductCategoryId));
          const sortedCategories = [...filteredCategories].sort((a, b) => a.name.localeCompare(b.name));
          setProductCategoriesList(sortedCategories);

          if (sortedCategories.length > 0) {
            const firstCategoryId = sortedCategories[0].mProductCategoryId;
            setSelectCategoryList([firstCategoryId]);
            setFilteredProducts(products.filter((product) => product.mProductCategoryId === firstCategoryId));
          }
        } else {
          console.warn("⚠️ No products found in IndexedDB.");
          message.warning("No products found in local database");
        }
      } catch (error) {
        console.error("❌ Error fetching data from IndexedDB:", error);
        message.error("Failed to load products or categories");
      }
    };

    fetchProductsAndCategories();
  }, []);

  const handleCategoryClick = (categoryId) => {
    setSelectCategoryList([categoryId]);
    const filtered = productsList.filter((product) => product.mProductCategoryId === categoryId);
    setFilteredProducts(filtered);
  };

  // Reset order history flag when customer changes
  useEffect(() => {
    hasLoadedOrderHistory.current = false;
  }, [cart?.customer?.cwrCustomerId]);

  const loadOrderHistory = async () => {
    if (!callCenterVisible || hasLoadedOrderHistory.current) return;

    hasLoadedOrderHistory.current = true;
    setLoading(true);
    try {
      const today = [dayjs(new Date()), dayjs(new Date())];
      await searchOrderHistory("orderDateSearchKey", today, 0, false, true);
    } catch (err) {
      console.error(err);
      message.error("Failed to load order history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (callCenterVisible && cart?.customer?.cwrCustomerId) {
      hasLoadedOrderHistory.current = false;
      loadOrderHistory();
    }
  }, [callCenterVisible, cart?.customer?.cwrCustomerId]);

  useEffect(() => {
    hasLoadedOrderHistory.current = false;
  }, [cart?.customer?.cwrCustomerId]);

  // Fetch retail stores
  const fetchRetailStores = async () => {
    try {
      const stores = await getRetailStores();
      if (stores.length > 0) {
        setRetailStores(stores);
        const currentStoreValue = tillData?.tillAccess?.csBunit?.value;
        const defaultStore = stores.find((s) => s.value === currentStoreValue) || stores[0];
        setSelectedStore(defaultStore);
        setCart((prev) => ({ ...prev, selectedStore: defaultStore }));
      } else {
        message.warning("No stores found");
      }
    } catch (err) {
      message.error("Failed to load stores");
    }
  };

  useEffect(() => {
    if (callCenterVisible && !storesLoaded.current) {
      storesLoaded.current = true;
      fetchRetailStores();
    }

    if (cart?.selectedStore) {
      setSelectedStore(cart.selectedStore);
    }
  }, [callCenterVisible, cart?.selectedStore]);

  return {
    productsList,
    filteredProducts,
    setFilteredProducts,
    productCategoriesList,
    selectCategoryList,
    setSelectCategoryList,
    displayedProducts,
    hasMoreProducts,
    retailStores,
    setRetailStores,
    selectedStore,
    setSelectedStore,
    storeModalVisible,
    setStoreModalVisible,
    scrollContainerRef,
    handleCategoryClick,
    loadOrderHistory,
  };
};

export default useCallCenterCustomer;
